'use client'

import React from 'react'

import { twMerge } from 'tailwind-merge'

import { formatStableDate } from '@/helpers/ssr/dates'

import { Timeline } from '../TimelinePrimitive'

// ============================================================================
// Types
// ============================================================================

interface TimelineV2Props {
  children: React.ReactNode
  className?: string
}

interface TimelineNodeProps {
  date: string | Date
  label: string
  status: 'past' | 'current' | 'future'
  className?: string
}

// ============================================================================
// Utility
// ============================================================================

function formatDate(date: string | Date): string {
  return formatStableDate(date) ?? '—'
}

// ============================================================================
// Style mappings
// ============================================================================

const dotStyles = {
  past: 'border-white bg-primary-400 dark:border-primary-800',
  current:
    'border-white bg-accent-600 ring-2 ring-accent-100 dark:ring-accent-900',
  future:
    'border-white bg-primary-200 dark:border-primary-800 dark:bg-primary-600',
} as const

const labelStyles = {
  past: 'text-primary-500 dark:text-primary-400',
  current: 'text-accent-600 dark:text-accent-400',
  future: 'text-primary-500 dark:text-primary-400',
} as const

const dateStyles = {
  past: 'text-primary-600 dark:text-primary-400',
  current: 'text-primary-900 dark:text-primary-100',
  future: 'text-primary-600 dark:text-primary-400',
} as const

// ============================================================================
// Constants for layout
// ============================================================================

// Dot is h-3 (12px) at top-[2px], so center is at 2px + 6px = 8px
const DOT_CENTER_Y = 8
// Dot is w-3 (12px) starting at left-0, so center is at 6px
// But we use 5px for the line to account for the 2px border
const LINE_X = 5

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * TimelineV2.Node - Individual timeline event
 *
 * Pure presentation component using Timeline primitive for layout.
 */
const Node: React.FC<TimelineNodeProps> = ({
  date,
  label,
  status,
  className,
}) => (
  <Timeline.Item
    dotCenterY={DOT_CENTER_Y}
    lineX={LINE_X}
    className={twMerge('pl-6', className)}
  >
    <Timeline.Dot
      className={twMerge(
        'left-0 top-[2px] h-3 w-3 border-2',
        dotStyles[status],
      )}
    />
    <Timeline.Content>
      <span
        className={twMerge(
          'mb-0.5 text-xs font-bold uppercase tracking-wide',
          labelStyles[status],
        )}
      >
        {label}
      </span>
      <span className={twMerge('text-xs font-medium', dateStyles[status])}>
        {formatDate(date)}
      </span>
    </Timeline.Content>
  </Timeline.Item>
)

Node.displayName = 'TimelineV2.Node'

// ============================================================================
// Main Component
// ============================================================================

/**
 * TimelineV2 - Vertical timeline for key dates
 *
 * Displays a sequence of events with past/current/future states.
 * Used in sidebar widgets to show solicitation key dates.
 *
 * @example
 * ```tsx
 * <TimelineV2>
 *   <TimelineV2.Node date="2024-01-15" label="Posted" status="past" />
 *   <TimelineV2.Node date="2024-02-01" label="Open" status="current" />
 *   <TimelineV2.Node date="2024-03-15" label="Deadline" status="future" />
 * </TimelineV2>
 * ```
 */
const TimelineV2: React.FC<TimelineV2Props> & {
  Node: typeof Node
} = ({ children, className }) => (
  <Timeline.Root className={className}>{children}</Timeline.Root>
)

TimelineV2.displayName = 'TimelineV2'
TimelineV2.Node = Node

// ============================================================================
// Skeleton Sub-Component
// ============================================================================

interface TimelineSkeletonProps {
  /** Number of nodes to render */
  nodeCount?: number
  className?: string
}

/**
 * Skeleton node for loading state - uses primitive directly
 * since we need explicit index control
 */
const SkeletonNode: React.FC<{ className?: string }> = ({ className }) => (
  <Timeline.Item
    dotCenterY={DOT_CENTER_Y}
    lineX={LINE_X}
    className={twMerge('pl-6', className)}
  >
    <Timeline.Dot className="left-0 top-[2px] h-3 w-3 animate-pulse bg-primary-200 dark:bg-primary-700" />
    <Timeline.Content className="gap-1">
      <div className="h-2.5 w-20 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
      <div className="h-4 w-28 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
    </Timeline.Content>
  </Timeline.Item>
)

/**
 * TimelineV2.Skeleton - Loading skeleton matching timeline layout
 *
 * @example
 * ```tsx
 * <TimelineV2.Skeleton nodeCount={4} />
 * ```
 */
const TimelineSkeleton: React.FC<TimelineSkeletonProps> = ({
  nodeCount = 4,
  className,
}) => (
  <Timeline.Root className={className}>
    {Array.from({ length: nodeCount }, (_, i) => (
      <SkeletonNode key={i} />
    ))}
  </Timeline.Root>
)

TimelineSkeleton.displayName = 'TimelineV2.Skeleton'

// Attach skeleton to TimelineV2
const TimelineV2WithSkeleton = TimelineV2 as typeof TimelineV2 & {
  Skeleton: typeof TimelineSkeleton
}
TimelineV2WithSkeleton.Skeleton = TimelineSkeleton

export { TimelineV2WithSkeleton as TimelineV2 }
export type { TimelineV2Props, TimelineNodeProps, TimelineSkeletonProps }
